import React, { useEffect } from 'react';
import LandingHeader from '../00-shared/LandingHeader';
import LandingFooter from '../00-shared/LandingFooter';
import { GlobalStyles } from '../00-shared/styles';
import { highlightColor } from '../00-shared/constants';

interface LegalSubsection {
    subheading: string;
    content: string;
}

interface LegalSection {
    heading: string;
    content?: string;
    subsections?: LegalSubsection[];
}

interface LegalDocument {
    title: string;
    lastUpdated?: string;
    sections: LegalSection[];
}

interface LegalPageProps {
    document: LegalDocument;
}

const LegalPage: React.FC<LegalPageProps> = ({ document: doc }) => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div
            className="min-h-screen bg-black text-white transition-colors duration-300"
            style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif" }}
        >
            <GlobalStyles />
            <LandingHeader highlightColor={highlightColor} />

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
                <header className="mb-12 border-b border-neutral-800 pb-8">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                        {doc.title}
                    </h1>
                    {doc.lastUpdated && (
                        <p className="text-sm text-gray-400">{doc.lastUpdated}</p>
                    )}
                </header>

                <div className="space-y-8">
                    {doc.sections.map((section, index) => (
                        <section key={index}>
                            <h2 className="text-xl md:text-2xl font-bold text-white mb-3">
                                {section.heading}
                            </h2>
                            {section.subsections ? (
                                <div className="space-y-4">
                                    {section.subsections.map((sub, subIndex) => (
                                        <div key={subIndex}>
                                            <h3 className="text-lg font-semibold text-white mb-2">
                                                {sub.subheading}
                                            </h3>
                                            <p className="text-gray-300 leading-relaxed">{sub.content}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-300 leading-relaxed">{section.content}</p>
                            )}
                        </section>
                    ))}
                </div>
            </main>

            <LandingFooter highlightColor={highlightColor} />
        </div>
    );
};

export default LegalPage;
